import { useEffect } from "react";
import ArticleFooterCTA from "../components/ArticleFooterCTA";

export default function Stack() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="article-stack" className="px-6 md:px-12 lg:px-24">
      <article className="max-w-3xl mx-auto pt-20 md:pt-32 pb-12 relative">
        <div
          aria-hidden="true"
          className="glow-orb orb-drift hidden md:block"
          style={{
            width: "420px",
            height: "420px",
            top: "-80px",
            right: "-180px",
            opacity: 0.35,
          }}
        />
        <header className="relative border-b border-subtle pb-12 mb-12">
          <span className="fade-up sticker sticker-tilt-left inline-flex">
            <span
              className="w-1.5 h-1.5 rounded-full pulse-dot"
              style={{ background: "var(--accent)" }}
            />
            Field Note · 03 · AI Tools
          </span>
          <h1 className="fade-up-1 mt-5 font-display text-primary text-4xl md:text-6xl font-light tracking-tight leading-[1.04]">
            The Solopreneur AI Stack
          </h1>
          <p className="fade-up-2 mt-6 text-secondary text-lg md:text-xl leading-relaxed max-w-2xl">
            A curated breakdown of the design-forward AI tools letting
            one-person studios out-ship entire teams — without hiring, agency
            retainers, or feature bloat.
          </p>
          <div className="fade-up-3 mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.22em] text-tertiary">
            <span>10 min read</span>
            <span>·</span>
            <span>For solo studios</span>
            <span>·</span>
            <span>Updated Feb 2026</span>
          </div>
        </header>

        <div className="prose-editorial fade-up-4">
          <p>
            A good AI stack isn&rsquo;t a list of tools. It&rsquo;s a set of
            decisions about which parts of your work get a permanent shadow
            collaborator. Pick eight tools and use them daily. That&rsquo;s the
            entire game.
          </p>

          <p>
            The stack below is the one I&rsquo;d rebuild from scratch tomorrow
            if I lost access to everything. Every tool earns its slot on two
            criteria: design taste and one-shot value. Skip either filter and
            your stack quietly becomes a billing problem.
          </p>

          <blockquote>
            Eight sharp tools beat thirty subscriptions every quarter.
          </blockquote>

          <h2>The Nine Slots</h2>

          <h3>1 · Thinking Partner</h3>
          <p>
            <strong>Claude or ChatGPT</strong>, used in long-form chat mode.
            Not for output — for unblocking. Paste your half-finished plan.
            Ask it where the holes are. Better than a coach for 80% of solo
            decisions because it never tires of your context.
          </p>
          <p>
            The skill here isn&rsquo;t prompting. It&rsquo;s knowing when to
            close the chat. Use it to surface options. Make the call yourself.
          </p>

          <h3>2 · Writing Surface</h3>
          <p>
            <strong>Lex</strong> or <strong>Cursor (for prose)</strong>. A
            writing app with AI baked into the cursor — not bolted on. Lex
            specifically rewards a clean, distraction-free aesthetic and
            inline suggestions you can accept or ignore without ceremony.
            Anything that opens a side panel breaks the flow.
          </p>

          <h3>3 · Visual Generation</h3>
          <p>
            <strong>Midjourney</strong> for hero visuals and pitch decks.{" "}
            <strong>Recraft</strong> for design-system-friendly illustrations
            you can actually use in product. The split matters: Midjourney
            paints, Recraft drafts. Don&rsquo;t use one for the other&rsquo;s
            job.
          </p>

          <h3>4 · Voice &amp; Notes</h3>
          <p>
            <strong>Granola</strong> or <strong>Audiopen</strong>. Record your
            thinking during walks. Voice-to-structured-note is the most
            underrated automation in any solo stack. Three months in,
            you&rsquo;ll have a private archive of every half-formed idea —
            tagged, searchable, and ready to mine.
          </p>

          <h3>5 · Research Engine</h3>
          <p>
            <strong>Perplexity</strong> for fast, sourced answers.{" "}
            <strong>Exa</strong> when you need depth and you want the raw
            paper or article behind the claim. Google has become an ad
            surface. These two restore the original promise.
          </p>

          <h3>6 · Code &amp; Build</h3>
          <p>
            <strong>Cursor</strong> for any code you touch.{" "}
            <strong>v0 or Lovable</strong> for one-shot UI scaffolds you can
            hand off to a developer or finish yourself. The leap here isn&rsquo;t
            that you become a developer — it&rsquo;s that you stop being
            blocked by small UI tasks.
          </p>

          <h3>7 · Visual Automation</h3>
          <p>
            <strong>Make.com</strong>. Covered in detail in the previous field
            note. The connective tissue that lets all the other tools talk to
            each other without you in the middle.
          </p>

          <h3>8 · Aesthetic Polish</h3>
          <p>
            <strong>Linear</strong> for tracking. <strong>Notion</strong> for
            architecture. <strong>Cron / Notion Calendar</strong> for time.
            These aren&rsquo;t AI tools strictly — they&rsquo;re the surfaces
            the AI tools deposit work into. The taste of your stack is the
            taste of where things land.
          </p>

          <h3>9 · The All-In-One Alternative</h3>
          <p>
            If wiring eight tools together sounds exhausting, skip the stack entirely. Use Systeme.io (<a href="/go/systeme" className="text-accent hover:underline">/go/systeme</a>). It replaces your website, emails, and checkout in one dashboard.
          </p>

          <h2>How To Run The Stack</h2>

          <ul>
            <li>
              <strong>One tool per slot</strong>. The fastest way to ruin an AI
              workflow is keeping two competing tools for the same job. Pick
              one. Delete the other.
            </li>
            <li>
              <strong>One paid tier per slot</strong>. Avoid the trap of being
              on the free tier of eight tools. You&rsquo;ll never trust them
              enough to commit. Pay the $20.
            </li>
            <li>
              <strong>One review per quarter</strong>. Every 90 days, ask one
              question per slot: &ldquo;Did this tool earn its spot?&rdquo; If
              not, swap. Never two at once.
            </li>
            <li>
              <strong>One workflow per tool</strong>. Master the single
              workflow that justifies the subscription before exploring
              anything else. Most tools have one killer feature. Find it.
            </li>
          </ul>

          <h2>Three Tools To Skip For Now</h2>

          <ul>
            <li>
              <strong>All-in-one AI suites</strong>. They&rsquo;re marketing
              bundles. The component tools are always weaker than the
              specialist version. Buy the specialist.
            </li>
            <li>
              <strong>Browser-extension AI</strong>. Adds noise to every page
              you visit. Sometimes useful, rarely worth the cognitive cost.
            </li>
            <li>
              <strong>Anything with &ldquo;agents&rdquo; in the headline</strong>.
              Agentic workflows aren&rsquo;t mature enough yet to run
              unattended for solo studios. Stay close to the loop. Wait six
              months.
            </li>
          </ul>

          <h2>The Real Cost Of A Lean Stack</h2>
          <p>
            Eight subscriptions runs roughly $180 a month. That&rsquo;s a
            fractional employee&rsquo;s tools at a five-figure salary, paid
            monthly, on your terms. A single client engagement covers the
            entire stack for a year. Stop pricing it against free
            alternatives. Price it against hiring.
          </p>

          <blockquote>
            The AI stack isn&rsquo;t a cost line. It&rsquo;s the cheapest
            headcount you&rsquo;ll ever afford.
          </blockquote>

          <h2>What This Buys You</h2>
          <p>
            The output of a small team without the overhead of one. A weekend
            where you can ship something most studios would quote for in
            six-week sprints. A practice of working with calm tools instead
            of against loud ones.
          </p>

          <p>
            Pick your eight. Pay for them. Run the quarterly review. Trust the
            compound effect of small, sharp tools deployed daily.
          </p>
        </div>

        <ArticleFooterCTA testId="stack-article" />
      </article>
    </main>
  );
}
